import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActiveCall, VoiceDeviceService, VoiceDeviceState } from '../services/voice-device.service';
import { CallDialerService } from '../services/call-dialer.service';
import { CallRoutingApiService } from '../services/call-routing-api.service';
import { CallAvailabilityApiService } from '../services/call-availability-api.service';
import { AgentAvailabilityResponse, TransferType } from '../models/call-routing.model';
import { ToastService } from '../../../shared/services/toast.service';
import { DrawerRegistryService } from '../../../shared/services/drawer-registry.service';
import { CallCrmContextPanelComponent } from '../call-crm-context-panel/call-crm-context-panel.component';
import { TRANSFER_TYPE_LABELS, labelFrom } from '../call-labels';

@Component({
  selector: 'app-call-dialer-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, CallCrmContextPanelComponent],
  templateUrl: './call-dialer-widget.component.html'
})
export class CallDialerWidgetComponent implements OnInit, OnDestroy {
  isOpen = false;
  deviceState: VoiceDeviceState = 'IDLE';
  activeCall: ActiveCall | null = null;
  lastError: string | null = null;

  anyDrawerOpen = false;

  toNumber = '';
  initiating = false;

  elapsed = '00:00';
  private elapsedTimer: ReturnType<typeof setInterval> | null = null;

  // Hold / Resume / Transfer
  holdInFlight = false;
  showTransferPanel = false;
  transferType: TransferType = 'AGENT';
  transferTargetId: number | null = null;
  transferring = false;
  agents: AgentAvailabilityResponse[] = [];
  loadingAgents = false;
  agentsLoadError: string | null = null;

  private readonly destroy$ = new Subject<void>();
  readonly e164Regex = /^\+[1-9]\d{1,14}$/;

  constructor(
    private readonly device: VoiceDeviceService,
    private readonly dialerService: CallDialerService,
    private readonly routingApi: CallRoutingApiService,
    private readonly availabilityApi: CallAvailabilityApiService,
    private readonly toast: ToastService,
    private readonly drawerRegistry: DrawerRegistryService
  ) {}

  ngOnInit(): void {
    // Inicializar el Device en cuanto el widget se monta (gate de rol lo hace el AppLayout).
    this.device.init();

    this.dialerService.state$.pipe(takeUntil(this.destroy$)).subscribe((s) => {
      this.isOpen = s.isOpen;
      if (s.prefill) this.toNumber = s.prefill;
    });

    this.device.deviceState$.pipe(takeUntil(this.destroy$)).subscribe((s) => {
      this.deviceState = s;
    });

    this.device.lastError$.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      this.lastError = e;
    });

    this.device.activeCall$.pipe(takeUntil(this.destroy$)).subscribe((c) => {
      this.activeCall = c;
      if (c?.phase === 'IN_CALL' && c.startedAt) {
        this.startElapsedTimer(c.startedAt);
      } else {
        this.stopElapsedTimer();
      }
    });

    this.drawerRegistry.anyOpen$.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      this.anyDrawerOpen = open;
    });

    // Si llega una llamada entrante, abrir el widget automáticamente.
    combineLatest([this.device.activeCall$, this.dialerService.state$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([call, ui]) => {
        if (call?.phase === 'INCOMING' && !ui.isOpen) {
          this.dialerService.open();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopElapsedTimer();
  }

  toggle(): void {
    this.dialerService.toggle();
  }

  close(): void {
    this.dialerService.close();
  }

  appendDigit(d: string): void {
    if (this.activeCall?.phase === 'IN_CALL') {
      // Durante una llamada los dígitos envían DTMF
      try { this.activeCall.call.sendDigits(d); } catch { /* ignore */ }
      return;
    }
    if (this.toNumber.length === 0 && d !== '+') {
      // Forzar prefijo + al inicio para E.164
      this.toNumber = '+' + d;
    } else {
      this.toNumber += d;
    }
  }

  backspace(): void {
    if (this.toNumber.length > 0) this.toNumber = this.toNumber.slice(0, -1);
  }

  clearNumber(): void { this.toNumber = ''; }

  isValidNumber(): boolean {
    return this.e164Regex.test(this.toNumber.trim());
  }

  isDeviceReady(): boolean {
    return this.deviceState === 'READY';
  }

  async llamar(): Promise<void> {
    const to = this.toNumber.trim();
    if (!this.isValidNumber()) {
      this.toast.warning({ title: 'Número inválido', message: 'Formato E.164 requerido (ej. +5117654321)' });
      return;
    }
    if (!this.isDeviceReady()) {
      this.toast.warning({ title: 'Dispositivo no listo', message: 'El device aún no se registró con Twilio.' });
      return;
    }

    this.initiating = true;
    // Una sola vía: el navegador origina la llamada y el webhook del TwiML la
    // registra en el CRM. Antes se llamaba ADEMÁS a `POST /api/calls`, que creaba
    // una segunda llamada de Twilio al mismo número: el destinatario recibía dos.
    // El `crmCallId` lo resuelve VoiceDeviceService por Call SID.
    try {
      await this.device.connect(to);
    } catch (err: unknown) {
      this.initiating = false;
      const msg = err instanceof Error ? err.message : 'No se pudo conectar el audio';
      this.toast.error({ title: 'Error', message: msg });
      return;
    }
    this.initiating = false;
  }

  aceptar(): void { this.device.accept(); }
  rechazar(): void { this.device.reject(); }
  colgar(): void { this.device.hangup(); }
  toggleMute(): void { this.device.toggleMute(); }

  canRoute(): boolean {
    return !!this.activeCall?.crmCallId && this.activeCall.phase === 'IN_CALL';
  }

  toggleHold(): void {
    const cur = this.activeCall;
    if (!cur?.crmCallId || this.holdInFlight) return;
    this.holdInFlight = true;
    const op$ = cur.isOnHold
      ? this.routingApi.resume(cur.crmCallId)
      : this.routingApi.hold(cur.crmCallId);
    op$.subscribe({
      next: () => {
        this.device.setOnHold(!cur.isOnHold);
        this.holdInFlight = false;
        this.toast.success({
          title: cur.isOnHold ? 'Llamada reanudada' : 'Llamada en espera',
          message: cur.isOnHold ? 'La llamada continúa.' : 'La llamada quedó en espera.'
        });
      },
      error: (err) => {
        this.holdInFlight = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo actualizar el estado de la llamada' });
      }
    });
  }

  abrirTransfer(): void {
    if (!this.canRoute()) return;
    this.showTransferPanel = true;
    this.transferType = 'AGENT';
    this.transferTargetId = null;
    this.loadAgents();
  }

  cerrarTransfer(): void {
    this.showTransferPanel = false;
    this.transferring = false;
  }

  private loadAgents(): void {
    this.loadingAgents = true;
    this.agentsLoadError = null;
    this.availabilityApi.listAvailableAgents().subscribe({
      next: (list) => {
        this.agents = Array.isArray(list) ? list : [];
        this.loadingAgents = false;
      },
      error: (err) => {
        this.agents = [];
        this.loadingAgents = false;
        this.agentsLoadError = err.error?.message || 'No se pudo cargar la lista de agentes';
      }
    });
  }

  seleccionarAgente(a: AgentAvailabilityResponse): void {
    this.transferType = 'AGENT';
    this.transferTargetId = a.userId;
  }

  ejecutarTransfer(): void {
    const cur = this.activeCall;
    if (!cur?.crmCallId || this.transferTargetId == null) return;
    this.transferring = true;
    this.routingApi
      .transfer(cur.crmCallId, { targetId: this.transferTargetId, transferType: this.transferType })
      .subscribe({
        next: () => {
          this.transferring = false;
          this.showTransferPanel = false;
          this.toast.success({ title: 'Llamada transferida', message: 'La llamada fue transferida correctamente.' });
        },
        error: (err) => {
          this.transferring = false;
          this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo transferir la llamada' });
        }
      });
  }

  statusColor(): string {
    switch (this.deviceState) {
      case 'READY': return 'bg-success-500';
      case 'REGISTERING': return 'bg-warning-500 animate-pulse';
      case 'NO_CONFIG':
      case 'IDLE': return 'bg-gray-400';
      case 'OFFLINE': return 'bg-gray-400';
      case 'ERROR': return 'bg-error-500';
      default: return 'bg-gray-400';
    }
  }

  transferTypeLabel(): string {
    return labelFrom(TRANSFER_TYPE_LABELS, this.transferType);
  }

  statusLabel(): string {
    switch (this.deviceState) {
      case 'READY': return 'Listo';
      case 'REGISTERING': return 'Conectando...';
      case 'NO_CONFIG': return 'Twilio no configurado';
      case 'IDLE': return 'Inactivo';
      case 'OFFLINE': return 'Desconectado';
      case 'ERROR': return 'Error';
    }
  }

  private startElapsedTimer(startedAt: number): void {
    this.stopElapsedTimer();
    const tick = () => {
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      const m = Math.floor(secs / 60).toString().padStart(2, '0');
      const s = (secs % 60).toString().padStart(2, '0');
      this.elapsed = `${m}:${s}`;
    };
    tick();
    this.elapsedTimer = setInterval(tick, 1000);
  }

  private stopElapsedTimer(): void {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
      this.elapsedTimer = null;
    }
    this.elapsed = '00:00';
  }

  readonly dialPad: { d: string; sub?: string }[][] = [
    [{ d: '1' }, { d: '2', sub: 'ABC' }, { d: '3', sub: 'DEF' }],
    [{ d: '4', sub: 'GHI' }, { d: '5', sub: 'JKL' }, { d: '6', sub: 'MNO' }],
    [{ d: '7', sub: 'PQRS' }, { d: '8', sub: 'TUV' }, { d: '9', sub: 'WXYZ' }],
    [{ d: '*' }, { d: '0', sub: '+' }, { d: '#' }]
  ];
}
